import { useCallback, useMemo } from 'react';
import { PROFESSIONAL_STEP_CONFIGS } from './steps/professional-steps';
import {
    useHDERegistrationForm,
    type FilterVisibleStepsContext,
} from '../../hooks/useHDERegistrationForm';
import {
    filterVisibleProExhibitorSteps,
    createGetInitialDataForStep,
    createBuildSubmitPayload,
    getSelectedProfessionalUrlSegment,
    type HDEProfessionalProfileWithHubspot,
} from './shared-registration-logic';
import type { LocationOption } from './types';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';

interface ProfessionalRegistrationFormProps {
    locationOptions: LocationOption[];
    layout?: 'single' | 'split';
    onClose?: () => void;
    onStepChange?: (stepId: string) => void;
    onAuthViewChange?: (showEmailForm: boolean) => void;
    onAuthLoadingChange?: (loading: boolean) => void;
    socialLoginSubmitted?: boolean;
    socialLoginResumed?: boolean;
    socialLoginEventId?: string | null;
    onBackToApplicationType?: () => void;
}

interface FormData {
    isProfessionalMember: boolean | null;
    selectedProfessionalId: string | null;
    eventId: string | null;
    fullName: string;
    companyName: string;
    companyWebsite: string;
    companyEmail: string;
    phone: string;
    companyType: string | null;
    companyTypeOther: string;
    companyLocation: string;
    companySelectedLocation: DetailedPlace | null;
    companyShowOtherInput: boolean;
    bringGuest: boolean | null;
    guestFormView: boolean;
    guestName: string;
    guestEmail: string;
    sameContactDetails: boolean;
}

const INITIAL_FORM_DATA: FormData = {
    isProfessionalMember: null,
    selectedProfessionalId: null,
    eventId: null,
    fullName: '',
    companyName: '',
    companyWebsite: '',
    companyEmail: '',
    phone: '',
    companyType: null,
    companyTypeOther: '',
    companyLocation: '',
    companySelectedLocation: null,
    companyShowOtherInput: false,
    bringGuest: null,
    guestFormView: false,
    guestName: '',
    guestEmail: '',
    sameContactDetails: false,
};

export function ProfessionalRegistrationForm({
    locationOptions,
    layout = 'single',
    onClose,
    onStepChange,
    onAuthViewChange,
    onAuthLoadingChange,
    socialLoginSubmitted = false,
    socialLoginResumed = false,
    socialLoginEventId = null,
    onBackToApplicationType,
}: ProfessionalRegistrationFormProps) {
    const initialFormData = useMemo(
        () =>
            socialLoginResumed
                ? {
                      ...INITIAL_FORM_DATA,
                      isProfessionalMember: true as const,
                      eventId: socialLoginEventId ?? null,
                  }
                : INITIAL_FORM_DATA,
        [socialLoginResumed, socialLoginEventId],
    );
    const filterVisibleSteps = useCallback(
        (
            configs: typeof PROFESSIONAL_STEP_CONFIGS,
            context: FilterVisibleStepsContext,
        ) => filterVisibleProExhibitorSteps(configs, context),
        [],
    );

    const getInitialDataForStep = useMemo(
        () =>
            createGetInitialDataForStep<FormData>({
                applicationType: 'Professional',
                socialLoginEventId,
                getGuestInvitationData: (formData, ctx) => ({
                    bringGuest: formData.bringGuest,
                    guestFormView: formData.guestFormView,
                    guestName: formData.guestName,
                    guestEmail: formData.guestEmail,
                    sameContactDetails: formData.sameContactDetails,
                    user: ctx.user,
                }),
                getSuccessStepExtras: (formData, selectedProfile) => {
                    const selectedProfileWithHubspot =
                        selectedProfile as HDEProfessionalProfileWithHubspot | null;
                    const clientDirector =
                        selectedProfileWithHubspot?.HubspotCompany
                            ?.ClientDirectorTeamMember ?? null;

                    return {
                        companyName:
                            formData.companyName ||
                            selectedProfile?.Title ||
                            'your business',
                        clientDirector,
                        clientDirectorDisplayName:
                            clientDirector?.Name?.trim() ||
                            'Client Director to be assigned',
                    };
                },
            }),
        [socialLoginEventId],
    );

    const buildSubmitPayload = useMemo(
        () =>
            createBuildSubmitPayload<FormData>({
                applicationType: 'Professional',
                locationOptions,
                appendGuestFields: (formData, submitData) => {
                    submitData.append(
                        'BringingGuest',
                        formData.bringGuest === true ? 'true' : 'false',
                    );
                    if (formData.bringGuest === true) {
                        if (formData.guestEmail) {
                            submitData.append(
                                'GuestEmail',
                                formData.guestEmail,
                            );
                        }
                        if (formData.guestName) {
                            const nameParts = formData.guestName
                                .trim()
                                .split(/\s+/)
                                .filter(Boolean);
                            const firstName = nameParts[0] || '';
                            const lastName =
                                nameParts.slice(1).join(' ').trim() ||
                                firstName;
                            if (firstName)
                                submitData.append('GuestFirstName', firstName);
                            if (lastName)
                                submitData.append('GuestLastName', lastName);
                        }
                    }
                },
            }),
        [locationOptions],
    );

    const form = useHDERegistrationForm<FormData>({
        initialFormData,
        stepConfigs: PROFESSIONAL_STEP_CONFIGS,
        layout,
        filterVisibleSteps,
        getInitialDataForStep,
        buildSubmitPayload,
        hasGuestInvitation: true,
        locationOptions,
        onClose,
        onStepChange,
        onAuthViewChange,
        onAuthLoadingChange,
        socialLoginSubmitted,
        onBackToApplicationType,
        indicatorStepOffset: 1,
        applicationType: 'Professional',
        getSelectedProfessionalUrlSegment: (fd, u) =>
            getSelectedProfessionalUrlSegment(fd, u),
    });

    return form.renderForm();
}
